import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useAuth } from '@/contexts/AuthContext';

const roleLabels: Record<string, string> = {
  student: '学生',
  teacher: '教师',
  parent: '家长',
  merchant: '商家',
};

export default function ProfilePage() {
  const [activeTab, setActiveTab] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', phone: '', grade: '', region: '', bio: '' });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const { user, profile, signOut, updateProfile, loading } = useAuth();
  const navigate = useNavigate();

  const isTeacher = profile?.role === 'teacher';
  const tabList = isTeacher
    ? ['我的课程', '我的订单', '教师工作台', '我的好友', '账号设置']
    : ['我的课程', '我的订单', '我的好友', '账号设置'];

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const startEdit = () => {
    setEditForm({
      name: profile?.name || '',
      phone: profile?.phone || '',
      grade: profile?.grade || '',
      region: profile?.region || '',
      bio: profile?.bio || '',
    });
    setEditMode(true);
    setSaveMsg('');
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const { error } = await updateProfile(editForm);
    setSaving(false);
    if (error) {
      setSaveMsg('保存失败：' + error);
    } else {
      setSaveMsg('保存成功！');
      setEditMode(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#faf9f7] flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-red-500 animate-spin block mb-3"></i>
          <p className="text-gray-400 text-sm">加载中...</p>
        </div>
      </div>
    );
  }

  if (!user || !profile) {
    return (
      <div className="min-h-screen bg-[#faf9f7] flex items-center justify-center">
        <div className="text-center">
          <i className="ri-user-line text-5xl text-gray-300 block mb-4"></i>
          <p className="text-gray-500 mb-6">请先登录查看个人主页</p>
          <Link to="/login" className="px-8 py-3 bg-red-600 text-white font-semibold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap inline-block">
            去登录
          </Link>
        </div>
      </div>
    );
  }

  const avatarLetter = profile.name ? profile.name[0].toUpperCase() : 'U';

  // Resolve which logical tab is active based on role
  const getTabKey = () => {
    if (isTeacher) {
      if (activeTab === 0) return 'courses';
      if (activeTab === 1) return 'orders';
      if (activeTab === 2) return 'teacher';
      if (activeTab === 3) return 'friends';
      return 'settings';
    }
    if (activeTab === 0) return 'courses';
    if (activeTab === 1) return 'orders';
    if (activeTab === 2) return 'friends';
    return 'settings';
  };

  const tabKey = getTabKey();

  return (
    <div className="min-h-screen bg-[#faf9f7]">
      <Navbar />
      <div className="pt-20">
        {/* Profile Header */}
        <div className="bg-gradient-to-br from-red-700 to-red-900">
          <div className="max-w-5xl mx-auto px-4 md:px-8 pt-10 pb-0">
            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-5 pb-6">
              <div className="relative">
                {profile.avatar_url ? (
                  <img src={profile.avatar_url} alt={profile.name} className="w-20 h-20 rounded-full object-cover border-4 border-white/30" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-amber-400 border-4 border-white/30 flex items-center justify-center">
                    <span className="text-white font-black text-3xl">{avatarLetter}</span>
                  </div>
                )}
                <div className="absolute bottom-0 right-0 w-5 h-5 flex items-center justify-center bg-green-500 rounded-full border-2 border-white"></div>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-white font-black text-2xl">{profile.name || '未设置昵称'}</h1>
                  <span className="bg-amber-400/20 border border-amber-400/40 text-amber-300 text-xs px-3 py-1 rounded-full">
                    {roleLabels[profile.role] || profile.role}
                    {profile.grade ? ` · ${profile.grade}` : ''}
                  </span>
                  {isTeacher && (
                    <span className="bg-green-500/20 border border-green-400/40 text-green-300 text-xs px-3 py-1 rounded-full">
                      认证教师
                    </span>
                  )}
                </div>
                <p className="text-white/60 text-sm mt-1">
                  {profile.region || '未设置地区'} · 加入于 {new Date(profile.created_at).toLocaleDateString('zh-CN', { year: 'numeric', month: 'long' })}
                </p>
                {profile.bio && <p className="text-white/50 text-xs mt-1">{profile.bio}</p>}
                <p className="text-white/40 text-xs mt-1">{user.email}</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                {isTeacher && (
                  <Link
                    to="/teacher/dashboard"
                    className="px-5 py-2 bg-amber-500 hover:bg-amber-400 text-white text-sm font-bold rounded-full cursor-pointer transition-colors whitespace-nowrap"
                  >
                    <i className="ri-dashboard-line mr-1"></i>教师工作台
                  </Link>
                )}
                <button
                  onClick={startEdit}
                  className="px-5 py-2 bg-white/15 border border-white/30 text-white text-sm rounded-full cursor-pointer hover:bg-white/25 transition-colors whitespace-nowrap"
                >
                  <i className="ri-edit-line mr-1"></i>编辑资料
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 overflow-x-auto">
              {tabList.map((tab, i) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(i)}
                  className={`px-5 py-3 text-sm font-medium whitespace-nowrap cursor-pointer transition-all border-b-2 ${activeTab === i ? 'border-white text-white' : 'border-transparent text-white/50 hover:text-white/80'}`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">

          {/* 我的订单 */}
          {tabKey === 'orders' && (
            <div className="text-center py-16">
              <div className="w-20 h-20 flex items-center justify-center bg-orange-50 rounded-full mx-auto mb-5">
                <i className="ri-shopping-bag-line text-4xl text-orange-500"></i>
              </div>
              <h2 className="text-xl font-black text-gray-900 mb-2">我的订单</h2>
              <p className="text-gray-500 text-sm mb-6">查看商品购买记录和物流状态</p>
              <Link
                to="/orders"
                className="px-8 py-3.5 bg-red-600 text-white font-bold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap inline-block"
              >
                <i className="ri-list-check mr-2"></i>查看全部订单
              </Link>
            </div>
          )}

          {/* 我的课程 */}
          {tabKey === 'courses' && (
            <div className="text-center py-16">
              <i className="ri-play-circle-line text-5xl text-gray-300 mb-4 block"></i>
              <p className="text-gray-400 mb-4">还没有购买课程</p>
              <Link to="/videos" className="px-6 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap inline-block">
                浏览教学视频
              </Link>
            </div>
          )}

          {/* 教师工作台入口 */}
          {tabKey === 'teacher' && (
            <div className="text-center py-16">
              <div className="w-20 h-20 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-5">
                <i className="ri-dashboard-fill text-4xl text-amber-500"></i>
              </div>
              <h2 className="text-xl font-black text-gray-900 mb-2">教师工作台</h2>
              <p className="text-gray-500 text-sm mb-6">管理课程、查看收益、申请提现</p>
              <Link
                to="/teacher/dashboard"
                className="px-8 py-3.5 bg-amber-500 text-white font-bold rounded-full cursor-pointer hover:bg-amber-400 transition-colors whitespace-nowrap inline-block shadow-lg"
              >
                <i className="ri-arrow-right-line mr-2"></i>进入工作台
              </Link>
            </div>
          )}

          {/* 我的好友 */}
          {tabKey === 'friends' && (
            <div className="text-center py-16">
              <i className="ri-user-add-line text-5xl text-gray-300 mb-4 block"></i>
              <p className="text-gray-400 mb-4">还没有添加好友</p>
              <Link to="/community/student" className="px-6 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap inline-block">
                发现学伴
              </Link>
            </div>
          )}

          {/* 账号设置 */}
          {tabKey === 'settings' && (
            <div className="max-w-lg">
              <h2 className="font-black text-gray-900 text-lg mb-5">账号设置</h2>
              {editMode ? (
                <form onSubmit={handleSave} className="bg-white rounded-2xl p-6 flex flex-col gap-4">
                  {[
                    { label: '昵称', key: 'name', type: 'text', placeholder: '请输入昵称' },
                    { label: '手机号', key: 'phone', type: 'tel', placeholder: '请输入手机号' },
                    { label: '年级/科目', key: 'grade', type: 'text', placeholder: '如：高三 / 考研英语' },
                    { label: '所在地区', key: 'region', type: 'text', placeholder: '如：北京市海淀区' },
                  ].map(field => (
                    <div key={field.key}>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">{field.label}</label>
                      <input
                        type={field.type}
                        value={editForm[field.key as keyof typeof editForm]}
                        onChange={e => setEditForm({ ...editForm, [field.key]: e.target.value })}
                        className="w-full bg-gray-50 rounded-xl px-4 py-3 text-sm outline-none border border-gray-200 focus:border-red-400 transition-colors"
                        placeholder={field.placeholder}
                      />
                    </div>
                  ))}
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">个人简介</label>
                    <textarea
                      value={editForm.bio}
                      onChange={e => setEditForm({ ...editForm, bio: e.target.value })}
                      className="w-full bg-gray-50 rounded-xl px-4 py-3 text-sm outline-none resize-none border border-gray-200 focus:border-red-400 transition-colors"
                      rows={3}
                      placeholder="介绍一下自己..."
                      maxLength={200}
                    />
                  </div>
                  {saveMsg && (
                    <p className={`text-xs px-3 py-2 rounded-lg ${saveMsg.includes('成功') ? 'text-green-600 bg-green-50' : 'text-red-500 bg-red-50'}`}>
                      {saveMsg}
                    </p>
                  )}
                  <div className="flex gap-3">
                    <button type="button" onClick={() => setEditMode(false)} className="flex-1 py-3 border border-gray-200 text-gray-600 font-semibold rounded-full cursor-pointer hover:bg-gray-50 transition-colors whitespace-nowrap">取消</button>
                    <button type="submit" disabled={saving} className="flex-1 py-3 bg-red-600 text-white font-semibold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap disabled:opacity-60">
                      {saving ? '保存中...' : '保存修改'}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="bg-white rounded-2xl divide-y divide-gray-50">
                  {[
                    { icon: 'ri-user-line', label: '昵称', value: profile.name || '未设置' },
                    { icon: 'ri-mail-line', label: '邮箱', value: user.email || '' },
                    { icon: 'ri-phone-line', label: '手机号', value: profile.phone || '未设置' },
                    { icon: 'ri-book-open-line', label: '年级/科目', value: profile.grade || '未设置' },
                    { icon: 'ri-map-pin-line', label: '所在地区', value: profile.region || '未设置' },
                    { icon: 'ri-shield-user-line', label: '身份', value: roleLabels[profile.role] || profile.role },
                  ].map(item => (
                    <div key={item.label} className="flex items-center gap-4 px-5 py-4">
                      <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg flex-shrink-0">
                        <i className={`${item.icon} text-gray-500 text-sm`}></i>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-700">{item.label}</p>
                        <p className="text-xs text-gray-400">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {!editMode && (
                <button onClick={handleSignOut} className="mt-6 w-full py-3 border-2 border-red-200 text-red-600 font-semibold rounded-full cursor-pointer hover:bg-red-50 transition-colors whitespace-nowrap">
                  退出登录
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
